import { useCallback, useEffect, useRef, useState } from 'react'
import type { ethers } from 'ethers'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { BASE_CHAIN_ID } from 'utils/swap/addresses'
import { reportClientError } from 'utils/reportClientError'
import { readStockMarkets, type StockMarket } from 'utils/stocks/market'

const REFRESH_MS = 20_000
/**
 * How long a read may take before the page says so.
 *
 * Without this a rate-limited or slow provider leaves every value shimmering
 * indefinitely, which tells a visitor nothing and looks identical to a page
 * that is broken. ethers retries a throttled call with its own backoff, so
 * "slow" here can mean minutes; twenty seconds is already far past the 1.6 s
 * this read takes on a healthy node.
 */
const READ_TIMEOUT_MS = 20_000

export interface UseStockMarketsResult {
  markets: StockMarket[]
  loading: boolean
  /** True when the chain could not be read at all; the last good data stays on screen. */
  failed: boolean
  /** When the numbers on screen were read. */
  readAt: number | null
  refresh: () => void
}

/**
 * The board's numbers, refreshed while the page is open.
 *
 * Two properties matter and both are about not lying. A refresh that fails
 * leaves the previous reading in place and says so, rather than blanking a
 * page of prices because one call timed out — and a reading that arrives after
 * a newer one has landed is dropped, which is the same sequence guard the swap
 * quote uses, for the same reason: two requests in flight return in whatever
 * order the network decides.
 */
export function useStockMarkets(only?: string[]): UseStockMarketsResult {
  const [markets, setMarkets] = useState<StockMarket[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [readAt, setReadAt] = useState<number | null>(null)
  const [nonce, setNonce] = useState(0)
  const sequence = useRef(0)
  const key = only ? only.join(',') : ''

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const mine = ++sequence.current
      let timer: ReturnType<typeof setTimeout> | undefined
      try {
        const provider = getSimpleRpcProviderByChainId(BASE_CHAIN_ID) as ethers.providers.Provider
        const read = readStockMarkets(provider, key ? key.split(',') : undefined)
        const next = await Promise.race([
          read,
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error('timeout')), READ_TIMEOUT_MS)
          })
        ])
        if (cancelled || mine !== sequence.current) return
        setMarkets(next)
        setReadAt(Date.now())
        setFailed(false)
      } catch (error) {
        if (cancelled || mine !== sequence.current) return
        setFailed(true)
        /*
         * A board that cannot read the chain is the failure nobody reports:
         * the page still renders, the numbers are simply absent, and a visitor
         * leaves rather than telling us. It is recorded as handled — we know
         * exactly what it is, and it is almost always the RPC pool.
         */
        reportClientError({
          message: `stocks.readFailed: ${key || 'board'} — ${String(
            (error as { message?: unknown })?.message ?? error
          ).slice(0, 160)}`,
          kind: 'transaction',
          handled: true
        })
      } finally {
        if (timer) clearTimeout(timer)
        if (!cancelled && mine === sequence.current) setLoading(false)
      }
    }
    void run()
    const interval = setInterval(() => void run(), REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [key, nonce])

  return { markets, loading, failed, readAt, refresh }
}
